/**
 * /inbox — the default-org outreach inbox.
 *
 * Renders just the cross-job reply inbox (InboxThreePane) with NONE of the full
 * outreach-hub chrome (no Inbox/Outreach/Analytics tab bar). It lives directly
 * inside the minimal default-org shell (New session / Sessions / Jobs / Inbox).
 * `?thread=` deep-links a specific conversation, kept in the URL so it's
 * shareable / refresh-safe. The `outreach-skin` wrapper supplies the outreach
 * design tokens InboxThreePane expects (normally provided by OutreachHubLayout).
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InboxThreePane } from '@/modules/outreach-v2';

export function OutreachInboxPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const threadParam = searchParams.get('thread');
    const campaignParam = searchParams.get('campaign');
    const jobParam = searchParams.get('job');

    const handleThreadChange = useCallback(
        (id: string | null) => {
            const next = new URLSearchParams(searchParams);
            if (id) next.set('thread', id);
            else next.delete('thread');
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    // In-inbox campaign picker (drives ?campaign=). Picking a campaign drops any
    // deep-linked role scope so the two never fight.
    const handleSelectCampaign = useCallback(
        (id: string | null) => {
            const next = new URLSearchParams(searchParams);
            next.delete('job');
            if (id) next.set('campaign', id);
            else next.delete('campaign');
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    // Clear the campaign/job scope → back to the full cross-job inbox.
    const handleClearScope = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('campaign');
        next.delete('job');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    return (
        <div
            className="outreach-skin flex h-full min-h-0 flex-col"
            style={{ background: 'var(--canvas-base)' }}
        >
            <InboxThreePane
                initialThreadId={threadParam}
                onThreadChange={handleThreadChange}
                scopeCampaignId={campaignParam}
                scopeJobId={jobParam}
                onClearScope={handleClearScope}
                onSelectCampaign={handleSelectCampaign}
            />
        </div>
    );
}
